import { Copy, Mail, MessageCircle, Instagram } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useCopyFlash } from '@/components/patterns/CopiedFlash';

interface ShareBookingLinkProps {
  bookingUrl: string;
  businessName: string;
  compact?: boolean;
  showCopy?: boolean;
}

export function ShareBookingLink({ bookingUrl, businessName, compact = false, showCopy = true }: ShareBookingLinkProps) {
  const { t } = useTranslation(['dashboard']);
  const { copy } = useCopyFlash();

  const copyLink = () => {
    copy(bookingUrl);
    toast.success(t('dashboard:share.copied'));
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(`Book an appointment with ${businessName}: ${bookingUrl}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const shareEmail = () => {
    const subject = encodeURIComponent(`Book with ${businessName}`);
    const body = encodeURIComponent(`Book your appointment: ${bookingUrl}`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_self');
  };

  const copyForInstagram = () => {
    copy(bookingUrl);
    toast.success(t('dashboard:share.instagramCopied'));
  };

  if (compact) {
    return (
      <div className="flex items-center gap-0.5">
        {showCopy && (
          <button
            onClick={copyLink}
            className="p-2 hover:bg-surface-2 rounded-lg transition-colors"
            title={t('dashboard:share.copyLink')}
          >
            <Copy className="h-4 w-4 text-soft" />
          </button>
        )}
        <button
          onClick={shareWhatsApp}
          className="p-2 hover:bg-green-50 rounded-lg transition-colors"
          title={t('dashboard:share.whatsapp')}
        >
          <MessageCircle className="h-4 w-4 text-green-600" />
        </button>
        <button
          onClick={shareEmail}
          className="p-2 hover:bg-orange-50 rounded-lg transition-colors"
          title={t('dashboard:share.email')}
        >
          <Mail className="h-4 w-4 text-orange-600" />
        </button>
        <button
          onClick={copyForInstagram}
          className="p-2 hover:bg-pink-50 rounded-lg transition-colors"
          title={t('dashboard:share.instagram')}
        >
          <Instagram className="h-4 w-4 text-pink-600" />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <button
        onClick={copyLink}
        className="w-full flex items-center gap-3 p-3 rounded-lg border border-line hover:bg-surface-2 transition-colors text-left"
      >
        <div className="w-9 h-9 bg-brand-100 rounded-lg flex items-center justify-center shrink-0">
          <Copy className="h-4 w-4 text-brand-500" />
        </div>
        <div>
          <p className="text-body-sm font-medium text-ink">{t('dashboard:share.copyLink')}</p>
          <p className="text-body-xs text-soft truncate max-w-[220px]">{bookingUrl}</p>
        </div>
      </button>

      <button
        onClick={shareWhatsApp}
        className="w-full flex items-center gap-3 p-3 rounded-lg border border-line hover:bg-surface-2 transition-colors text-left"
      >
        <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center shrink-0">
          <MessageCircle className="h-4 w-4 text-green-600" />
        </div>
        <div>
          <p className="text-body-sm font-medium text-ink">{t('dashboard:share.whatsapp')}</p>
        </div>
      </button>

      <button
        onClick={shareEmail}
        className="w-full flex items-center gap-3 p-3 rounded-lg border border-line hover:bg-surface-2 transition-colors text-left"
      >
        <div className="w-9 h-9 bg-orange-100 rounded-lg flex items-center justify-center shrink-0">
          <Mail className="h-4 w-4 text-orange-600" />
        </div>
        <div>
          <p className="text-body-sm font-medium text-ink">{t('dashboard:share.email')}</p>
        </div>
      </button>

      <button
        onClick={copyForInstagram}
        className="w-full flex items-center gap-3 p-3 rounded-lg border border-line hover:bg-surface-2 transition-colors text-left"
      >
        <div className="w-9 h-9 bg-pink-100 rounded-lg flex items-center justify-center shrink-0">
          <Instagram className="h-4 w-4 text-pink-600" />
        </div>
        <div>
          <p className="text-body-sm font-medium text-ink">{t('dashboard:share.instagram')}</p>
        </div>
      </button>
    </div>
  );
}
